import {useMemo} from 'react';
import {useTestsState} from './useTestsQuery';
import {useDashboardData} from './useDashboardData';
import {
    getRecentBiomarkers,
    biomarkerGroupKey,
    cleanBiomarkerName,
    isNonBiomarkerField,
} from '@/shared/utils/biomarkerUtils';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';
import type {BiomarkerStatus} from '@/shared/types/biomarker';
import type {Test} from '@/shared/types/api';

/**
 * Every biomarker whose LATEST valid result is out of range or borderline,
 * derived from the same tests cache, admin-managed display rules, and
 * classifier the rest of the dashboard uses (no gender argument — the card
 * is scoped to severity, not sex-specific band selection) — so the
 * "Biomarkers Need Attention" card can never disagree with the trend chips.
 *
 * Deliberately independent of BioMatch: the card describes the patient's
 * results, not the matched kit. Which markers a recommendation's rules
 * happen to reference has no bearing on what needs attention (see
 * docs/superpowers/specs/2026-08-11-biomarkers-attention-card-all-abnormal-design.md).
 */

export interface AbnormalBiomarker {
    /** Canonical LOINC-first group key — stable across source aliases. */
    key: string;
    /** Cleaned display name (ELIS "01; " prefixes stripped). */
    name: string;
    status: BiomarkerStatus;
    tier: 'critical' | 'borderline';
}

export interface AbnormalBiomarkersResult {
    abnormal: AbnormalBiomarker[];
    counts: {outOfRange: number; borderline: number};
}

/** 500 ≫ any real patient's distinct biomarkers; getRecentBiomarkers keys
 *  per biomarker, so this is "all of them", not a display cap. */
const ALL_BIOMARKERS = 500;

export function deriveAbnormalBiomarkers(
    tests: Test[],
    rules: BiomarkerDisplayRule[] = []
): AbnormalBiomarkersResult {
    const abnormal: AbnormalBiomarker[] = [];
    for (const b of getRecentBiomarkers(tests, ALL_BIOMARKERS, undefined, rules)) {
        // OHD survey/operational rows (hrs_fasted, biometrics, …) arrive as
        // result rows but are not lab biomarkers; without a ref range they
        // classify against the 0-100 fallback and would chip as abnormal.
        if (isNonBiomarkerField(b.testName)) continue;
        // The biomarker's own classification tier — not severityTier(b.status)
        // — so a matched display-rule band's severity (which can diverge from
        // its label's default tier) is respected. See ProcessedBiomarker.tier.
        const tier = b.tier;
        if (tier !== 'critical' && tier !== 'borderline') continue;
        abnormal.push({
            key: biomarkerGroupKey({test_name: b.testName, loinc: b.loinc}),
            name: cleanBiomarkerName(b.testName),
            status: b.status,
            tier,
        });
    }
    // Out-of-range first; sort() is stable and the input is date-desc, so
    // recency order survives within each tier.
    abnormal.sort((a, b) => (a.tier === b.tier ? 0 : a.tier === 'critical' ? -1 : 1));
    const outOfRange = abnormal.filter((m) => m.tier === 'critical').length;
    return {
        abnormal,
        counts: {outOfRange, borderline: abnormal.length - outOfRange},
    };
}

export function useAbnormalBiomarkers(): AbnormalBiomarkersResult & {isLoading: boolean} {
    // Patient-scoped tests — same source Lab Trends uses. NOT the account-
    // wide useTestsState().tests: on multi-patient accounts (dependents)
    // that would mix family members' results into one card, and the LOINC
    // dedup could even suppress one patient's abnormal result with another
    // patient's newer normal one.
    const {data: tests} = useDashboardData();
    const {loading} = useTestsState();
    const {rules} = useDisplayRules();
    const derived = useMemo(() => deriveAbnormalBiomarkers(tests, rules), [tests, rules]);
    return {...derived, isLoading: loading};
}
